import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getOpenClawStatus } from 'helpers/requests/vms';
import { settings } from 'config';

const FileBrowserPanel = ({ vmId }) => {
  const [fbData, setFbData] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      let fbUrl = null;
      let fbUsername = null;
      let fbPassword = null;

      // Step 1: Wait for agent status to return FileBrowser info
      for (let i = 0; i < 40; i++) {
        if (cancelled) return;
        try {
          const r = await getOpenClawStatus(vmId);
          if (
            r.success &&
            r.response.data.filebrowser_url &&
            r.response.data.filebrowser_password
          ) {
            const apiOrigin = new URL(settings.APIBaseURL).origin;
            fbUrl = apiOrigin + r.response.data.filebrowser_url;
            fbUsername = r.response.data.filebrowser_username;
            fbPassword = r.response.data.filebrowser_password;
            break;
          }
        } catch (e) {
          console.error(e);
        }
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      if (!fbUrl || cancelled) {
        if (isMounted.current) setError('File browser unavailable');
        return;
      }

      // Step 2: Wait for FileBrowser to actually respond (Celery task may still be setting it up)
      for (let i = 0; i < 30; i++) {
        if (cancelled) return;
        try {
          const resp = await fetch(fbUrl);
          if (resp.ok) {
            if (!cancelled && isMounted.current) {
              setFbData({
                url: fbUrl,
                username: fbUsername,
                password: fbPassword
              });
            }
            return;
          }
        } catch (e) {
          console.error(e);
        }
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      if (isMounted.current) setError('File browser unavailable');
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [vmId]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (error) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100">
        <p className="text-muted">{error}</p>
      </div>
    );
  }

  if (!fbData) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100">
        <Spinner animation="border" className="mb-3" variant="light" />
        <p className="text-muted">Loading file browser...</p>
      </div>
    );
  }

  return (
    <div className="position-relative w-100 h-100">
      <iframe
        src={fbData.url}
        className="w-100 h-100 border-0"
        title="File Browser"
        allow="clipboard-read; clipboard-write"
      />

      <Card
        className="position-absolute bottom-0 end-0 mb-3 me-3 shadow-lg"
        style={{ zIndex: 10, width: 420 }}
      >
        <Card.Body className="p-3">
          <h6 className="mb-2 fs--1">
            <FontAwesomeIcon icon="key" className="me-1 text-warning" />
            Login Credentials
          </h6>
          <div className="d-flex align-items-center mb-2">
            <span className="text-muted fs--1" style={{ width: 70 }}>
              Username
            </span>
            <code className="fs--1 flex-grow-1">{fbData.username}</code>
            <Button
              variant="falcon-default"
              size="sm"
              className="ms-2 px-2 py-0"
              onClick={() => copyToClipboard(fbData.username, 'username')}
            >
              <FontAwesomeIcon
                icon={copiedField === 'username' ? 'check' : 'copy'}
                className="fs--2"
              />
            </Button>
          </div>
          <div className="d-flex align-items-center">
            <span className="text-muted fs--1" style={{ width: 70 }}>
              Password
            </span>
            <code className="fs--1 flex-grow-1">
              {showPassword ? fbData.password : '••••••••••••••••'}
            </code>
            <Button
              variant="falcon-default"
              size="sm"
              className="ms-2 px-2 py-0"
              onClick={() => setShowPassword(!showPassword)}
            >
              <FontAwesomeIcon
                icon={showPassword ? 'eye-slash' : 'eye'}
                className="fs--2"
              />
            </Button>
            <Button
              variant="falcon-default"
              size="sm"
              className="ms-1 px-2 py-0"
              onClick={() => copyToClipboard(fbData.password, 'password')}
            >
              <FontAwesomeIcon
                icon={copiedField === 'password' ? 'check' : 'copy'}
                className="fs--2"
              />
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FileBrowserPanel;
